import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ActionButton } from '@adsk/alloy-react-menu';
import { useLocale } from '@adsk/alloy-react-helpers';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { LinkButton } from '@adsk/alloy-react-button';
import {
  adminAccountChangeProjectStatusRequest,
  getAccountProjectOptionsMenuItems,
} from './utils';
import { menuStyle } from './styles';

const AccountProjectOptionsMenu = ({
  horizontal,
  onArchiveProject,
  onRestoreProject,
  projectId,
  projectName,
  projectStatus,
  style,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { showNotification } = useNotifications();

  const latestOnArchiveProject = useRef(onArchiveProject);
  const latestOnRestoreProject = useRef(onRestoreProject);

  const {
    data: archiveData,
    error: archiveError,
    doFetch: doFetchArchive,
  } = useDataApi();
  const {
    data: restoreData,
    error: restoreError,
    doFetch: doFetchRestore,
  } = useDataApi();

  const handleRestoreClick = () => {
    doFetchRestore(
      ...adminAccountChangeProjectStatusRequest(projectId, 'active')
    );
  };

  const handleArchiveClick = () => {
    doFetchArchive(
      ...adminAccountChangeProjectStatusRequest(projectId, 'archived')
    );
  };

  const archiveProjectCallback = useCallback(handleArchiveClick, [
    doFetchArchive,
    projectId,
  ]);

  const restoreProjectCallback = useCallback(handleRestoreClick, [
    doFetchRestore,
    projectId,
  ]);

  useEffect(() => {
    latestOnArchiveProject.current = onArchiveProject;
  }, [onArchiveProject]);

  useEffect(() => {
    latestOnRestoreProject.current = onRestoreProject;
  }, [onRestoreProject]);

  useEffect(() => {
    if (archiveData === null && archiveError === null) return;
    if (archiveData) {
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: (
          <span>
            {T.translate('ARCHIVE_PROJECT_SUCCESS', {
              projectName,
            })}
          </span>
        ),
      });

      latestOnArchiveProject.current(true);
    } else {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: (
          <span>
            {T.translate('ARCHIVE_PROJECT_ERROR')}{' '}
            <LinkButton onClick={archiveProjectCallback}>
              {T.translate('TRY_AGAIN')}
            </LinkButton>
          </span>
        ),
      });

      latestOnArchiveProject.current(false);
    }
  }, [
    archiveData,
    archiveError,
    archiveProjectCallback,
    projectName,
    showNotification,
    T,
  ]);

  useEffect(() => {
    if (restoreData === null && restoreError === null) return;

    if (restoreData) {
      showNotification({
        status: NOTIFICATION_STATUSES.SUCCESS,
        message: (
          <span>
            {T.translate('RESTORE_PROJECT_SUCCESS', {
              projectName,
            })}
          </span>
        ),
      });

      latestOnRestoreProject.current(true);
    } else {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: (
          <span>
            {T.translate('RESTORE_PROJECT_ERROR')}{' '}
            <LinkButton onClick={restoreProjectCallback}>
              {T.translate('TRY_AGAIN')}
            </LinkButton>
          </span>
        ),
      });

      latestOnRestoreProject.current(false);
    }
  }, [
    projectName,
    restoreData,
    restoreError,
    restoreProjectCallback,
    showNotification,
    T,
  ]);

  const handleActionButtonChange = ([option]) => {
    if (option === 'archive') handleArchiveClick();
    else if (option === 'restore') handleRestoreClick();
  };

  return (
    <ActionButton
      data-testid="ProjectMemberProfileOptionsMenu_icon"
      horizontal={horizontal}
      menuStyle={menuStyle}
      onChange={handleActionButtonChange}
      onClick={(e) => {
        e.stopPropagation();
      }}
      options={getAccountProjectOptionsMenuItems(
        projectStatus,
        T.translate('ARCHIVE_PROJECT'),
        T.translate('RESTORE_PROJECT')
      )}
      style={style}
    />
  );
};

AccountProjectOptionsMenu.propTypes = {
  /** Menu button with horizontal or vertical dots */
  horizontal: PropTypes.bool,
  /** Callback executed when the user clicks on the Archive project option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onArchiveProject: PropTypes.func,
  /** Callback executed when the user clicks on the Restore project option.<br>
   * A boolean argument will be passed to the callback to indicate if the request was successful.
   */
  onRestoreProject: PropTypes.func,
  /** Project Id */
  projectId: PropTypes.string.isRequired,
  /** Project Name */
  projectName: PropTypes.string.isRequired,
  /** Project status */
  projectStatus: PropTypes.string.isRequired,
  /** Styles applied to the menu button */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

AccountProjectOptionsMenu.defaultProps = {
  horizontal: false,
  onArchiveProject: () => {},
  onRestoreProject: () => {},
};

export default AccountProjectOptionsMenu;
